import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LogoContainer from "../../../../../shared/components/LogoContainer";
import InputForm from "../../../../../shared/components/InputForm";
import FormSelect from "../../../../../shared/components/FormSelect";
import AddAnotherList from "../../../../../shared/components/AddAnotherList";
import { createDebt } from "../../../../../../app/store/slices/debtSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import Spinner from "../../../../../shared/components/Spinner";
import {formatAmount} from '../../../../../../utils/formatAmount';

const DebtSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);

  const [debts, setDebts] = useState([]);
  const [currentDebt, setCurrentDebt] = useState({
    title: "",
    initialAmount: "",
    startDate: "",
    annualInterestRate: "",
    monthlyPayment: "",
    dueDate: "", // Keep this field
    amountPaid: "",
    paymentAccountId: location.state?.accountId || "",
    // Add minimal schedule fields
    dueDateType: "",
    dueDateDay: "",
    dueDateMonth: ""
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const [selectedCanvasId, setSelectedCanvasId] = useState(
    JSON.parse(localStorage.getItem("onboardingCanvas"))?._id ||
      location.state?.canvasId
  );

  const dueDateTypes = [
    { value: 'First', label: 'First' },
    { value: 'Last', label: 'Last' },
    { value: 'Specific', label: 'Specific' }
  ];

  const monthDays = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString()
  }));

  const months = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  const shouldShowDay = () => {
    return currentDebt.dueDateType === 'Specific';
  };

  const shouldShowMonth = () => {
    return currentDebt.dueDateType && currentDebt.dueDateType !== '';
  };

  const getCurrentMonth = () => {
    const today = new Date();
    return months[today.getMonth()].value;
  };

  const calculateNextDueDate = (dueDateType, dueDateMonth, dueDateDay) => {
    if (dueDateType && dueDateMonth) {
      const monthIndex = months.findIndex(m => m.value === dueDateMonth);
      const currentYear = new Date().getFullYear();
      const today = new Date();
      today.setHours(0, 0, 0, 0); 
      
      let dueDate;
      
      if (dueDateType === 'First') {
        dueDate = new Date(currentYear, monthIndex, 1);
      } else if (dueDateType === 'Last') {
        dueDate = new Date(currentYear, monthIndex + 1, 0);
      } else if (dueDateType === 'Specific' && dueDateDay) {
        dueDate = new Date(currentYear, monthIndex, parseInt(dueDateDay));
      }
      
      if (dueDate && dueDate < today) {
        if (dueDateType === 'First') {
          if (monthIndex === 11) { // December
            dueDate = new Date(currentYear + 1, 0, 1); 
          } else {
            dueDate = new Date(currentYear, monthIndex + 1, 1);
          }
        } else if (dueDateType === 'Last') {
          if (monthIndex === 11) { // December
            dueDate = new Date(currentYear + 1, 1, 0); 
          } else {
            dueDate = new Date(currentYear, monthIndex + 2, 0);
          }
        } else if (dueDateType === 'Specific') {
          dueDate = new Date(currentYear, monthIndex + 1, parseInt(dueDateDay));
          if (dueDate.getDate() !== parseInt(dueDateDay)) {
            dueDate = new Date(currentYear, monthIndex + 2, 0);
          }
        }
      }
            while (dueDate && dueDate < today) {
        if (dueDateType === 'First') {
          dueDate.setMonth(dueDate.getMonth() + 1, 1);
        } else if (dueDateType === 'Last') {
          dueDate = new Date(dueDate.getFullYear(), dueDate.getMonth() + 2, 0);
        } else if (dueDateType === 'Specific') {
          dueDate.setMonth(dueDate.getMonth() + 1);
          if (dueDate.getDate() !== parseInt(dueDateDay)) {
            dueDate = new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, 0);
          }
        }
      }
      
      return dueDate ? dueDate.toISOString().split('T')[0] : "";
    }
    return "";
  };

  const getPreviewText = () => {
    if (!currentDebt.dueDateType || !currentDebt.dueDateMonth) return '';
    
    const calculatedDate = calculateNextDueDate(currentDebt.dueDateType, currentDebt.dueDateMonth, currentDebt.dueDateDay);
    
    if (currentDebt.dueDateType === 'First') {
      return ` First day of every month (Next: ${calculatedDate ? new Date(calculatedDate).toLocaleDateString() : ''})`;
    } else if (currentDebt.dueDateType === 'Last') {
      return ` Last day of every month (Next: ${calculatedDate ? new Date(calculatedDate).toLocaleDateString() : ''})`;
    } else if (currentDebt.dueDateType === 'Specific') {
      if (!currentDebt.dueDateDay) return '';
      return ` Every ${currentDebt.dueDateDay} of the month (Next: ${calculatedDate ? new Date(calculatedDate).toLocaleDateString() : ''})`;
    }

    return '';
  };

  // Auto-calculate and set due date when schedule changes
  useEffect(() => {
    if (currentDebt.dueDateType && currentDebt.dueDateMonth) {
      const calculatedDate = calculateNextDueDate(currentDebt.dueDateType, currentDebt.dueDateMonth, currentDebt.dueDateDay);
      if (calculatedDate && calculatedDate !== currentDebt.dueDate) {
        setCurrentDebt(prev => ({ ...prev, dueDate: calculatedDate }));
      }
    }
  }, [currentDebt.dueDateType, currentDebt.dueDateMonth, currentDebt.dueDateDay]);

  // Set default month to current month when type is selected
  useEffect(() => {
    if (currentDebt.dueDateType && !currentDebt.dueDateMonth) {
      setCurrentDebt(prev => ({ ...prev, dueDateMonth: getCurrentMonth() }));
    }
  }, [currentDebt.dueDateType]);

  useEffect(() => {
    if (selectedCanvasId) {
      dispatch(fetchAccounts({ canvasId: selectedCanvasId, limit: 1000 }));
    }
  }, [dispatch, selectedCanvasId]);

  useEffect(() => {
    if (!selectedCanvasId && location.state?.canvasId) {
      setSelectedCanvasId(location.state.canvasId);
    }
  }, [location.state, selectedCanvasId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDebt({ ...currentDebt, [name]: value });
  };

  const handleAddDebt = async () => {
    if (
      currentDebt.title &&
      currentDebt.initialAmount &&
      currentDebt.dueDate &&
      currentDebt.paymentAccountId &&
      selectedCanvasId
    ) {
      try {
        await dispatch(
          createDebt({
            canvasId: selectedCanvasId,
            debtData: {
              title: currentDebt.title,
              initialAmount: Number(currentDebt.initialAmount),
              startDate: currentDebt.startDate
                ? new Date(currentDebt.startDate)
                : undefined,
              annualInterestRate:
                Number(currentDebt.annualInterestRate) || 0,
              monthlyPayment: Number(currentDebt.monthlyPayment) || 0,
              dueDate: new Date(currentDebt.dueDate),
              monthlyPaymentDueDate: new Date(currentDebt.dueDate),
              amountPaid: Number(currentDebt.amountPaid) || 0,
              paymentAccountId: currentDebt.paymentAccountId,
              dueDateType: currentDebt.dueDateType,
              dueDateDay: currentDebt.dueDateDay,
              dueDateMonth: currentDebt.dueDateMonth
            },
          })
        ).unwrap();

        setDebts([...debts, currentDebt]);
        setCurrentDebt({
          title: "",
          initialAmount: "",
          startDate: "",
          annualInterestRate: "",
          monthlyPayment: "",
          dueDate: "",
          amountPaid: "",
          paymentAccountId: location.state?.accountId || "",
          dueDateType: "",
          dueDateDay: "",
          dueDateMonth: ""
        });
      } catch (err) {
        console.error("Failed to create debt:", err);
      }
    }
  };

  const handleContinue = async () => {
    setIsProcessing(true);
    try {
      if (
        currentDebt.title &&
        currentDebt.initialAmount &&
        currentDebt.dueDate &&
        currentDebt.paymentAccountId &&
        selectedCanvasId
      ) {
        await dispatch(
          createDebt({
            canvasId: selectedCanvasId,
            debtData: {
              title: currentDebt.title,
              initialAmount: Number(currentDebt.initialAmount),
              startDate: currentDebt.startDate
                ? new Date(currentDebt.startDate)
                : undefined,
              annualInterestRate:
                Number(currentDebt.annualInterestRate) || 0,
              monthlyPayment: Number(currentDebt.monthlyPayment) || 0,
              dueDate: new Date(currentDebt.dueDate),
              monthlyPaymentDueDate: new Date(currentDebt.dueDate),
              amountPaid: Number(currentDebt.amountPaid) || 0,
              paymentAccountId: currentDebt.paymentAccountId,
              dueDateType: currentDebt.dueDateType,
              dueDateDay: currentDebt.dueDateDay,
              dueDateMonth: currentDebt.dueDateMonth
            },
          })
        ).unwrap();

        setDebts((prev) => [...prev, currentDebt]);
        setCurrentDebt({
          title: "",
          initialAmount: "",
          startDate: "",
          annualInterestRate: "",
          monthlyPayment: "",
          dueDate: "",
          amountPaid: "",
          paymentAccountId: location.state?.accountId || "",
          dueDateType: "",
          dueDateDay: "",
          dueDateMonth: ""
        });
      }

      navigate("/mortgage", {
        state: {
          accountId: currentDebt.paymentAccountId,
          canvasId: selectedCanvasId,
        },
      });
    } catch (err) {
      console.error("Failed to create debt:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer />
      <div className="card-setup">
        <h2 className="txt-page-heading">Enter your Debts</h2>
        <p className="p-tag-below-heading text-center">
          Enter your debts and repayment details
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            <InputForm
              label="Debt Title"
              type="text"
              id="debtTitle"
              name="title"
              value={currentDebt.title}
              onChange={handleInputChange}
              placeholder="eg Car Loan"
            />
            <InputForm
              label="Initial Amount"
              type="number"
              id="initialAmount"
              name="initialAmount"
              value={currentDebt.initialAmount}
              onChange={handleInputChange}
              placeholder="Initial amount"
              min="0"
            />
            <InputForm
              label="Start Date"
              type="date"
              id="startDate"
              name="startDate"
              value={currentDebt.startDate}
              onChange={handleInputChange}
            />
            <InputForm
              label="Interest Rate"
              type="number"
              id="annualInterestRate"
              name="annualInterestRate"
              value={currentDebt.annualInterestRate}
              onChange={handleInputChange}
              placeholder="Interest rate (%)"
              min="0"
              step="0.01"
            />
            <InputForm
              label="Monthly Payment"
              type="number"
              id="monthlyPayment"
              name="monthlyPayment"
              value={currentDebt.monthlyPayment}
              onChange={handleInputChange}
              placeholder="Monthly payment"
              min="0"
              step="0.01"
            />
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date Type
                  </label>
                  <select
                    value={currentDebt.dueDateType}
                    onChange={(e) => setCurrentDebt({ ...currentDebt, dueDateType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    {dueDateTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {shouldShowMonth() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Starting Month
                    </label>
                    <select
                      value={currentDebt.dueDateMonth || ''}
                      onChange={(e) => setCurrentDebt({ ...currentDebt, dueDateMonth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                    >
                      <option value="">Select Month</option>
                      {months.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {shouldShowDay() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day
                    </label>
                    <select
                      value={currentDebt.dueDateDay || ''}
                      onChange={(e) => setCurrentDebt({ ...currentDebt, dueDateDay: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                    >
                      <option value="">Select Day</option>
                      {monthDays.map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {(currentDebt.dueDateType && currentDebt.dueDateMonth) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Schedule Preview:</span>
                    {getPreviewText()}
                  </p>
                </div>
              )}
            </div>

            {/* <InputForm
              label="Monthly Due Date"
              type="date"
              id="dueDate"
              name="dueDate"
              value={currentDebt.dueDate}
              onChange={handleInputChange}
              className="bg-gray-50"
              placeholder="Auto-calculated from schedule above"
            /> */}

            <InputForm
              label="Amount Paid"
              type="number"
              id="amountPaid"
              name="amountPaid"
              value={currentDebt.amountPaid}
              onChange={handleInputChange}
              placeholder="Amount paid"
              min="0"
              step="0.01"
            />
            <FormSelect
              label="Account"
              id="paymentAccountId"
              name="paymentAccountId"
              value={currentDebt.paymentAccountId}
              onChange={handleInputChange}
              options={[
                { value: "", label: "Select Account" },
                ...(accounts || []).map((acc) => ({
                  value: acc._id,
                  label: `${acc.name} (${acc.currency})`,
                })),
              ]}
            />
          </div>

          <div className="mt-8 flex flex-row justify-center gap-6 w-full max-w-lg">
            <button
              type="button"
              className="btn-back flex-1"
              onClick={() => {
                setCurrentDebt({
                  title: "",
                  initialAmount: "",
                  startDate: "",
                  annualInterestRate: "",
                  monthlyPayment: "",
                  dueDate: "",
                  amountPaid: "",
                  paymentAccountId: location.state?.accountId || "",
                  dueDateType: "",
                  dueDateDay: "",
                  dueDateMonth: ""
                });
                navigate("/mortgage", {
                  state: {
                    accountId: currentDebt.paymentAccountId,
                    canvasId: selectedCanvasId,
                  },
                });
              }}
            >
              Skip
            </button>
            <button
              type="button"
              className="btn-continue flex-1"
              onClick={handleAddDebt}
            >
              Add Another Debt
            </button>
          </div>
        </div>

        <AddAnotherList
          title="Your Debts"
          items={debts}
          renderItem={(debt) => (
            <div>
              <div className="font-medium">{debt.title}</div>
              <div className="text-sm text-gray-500">
                {formatAmount(debt.initialAmount)}
              </div>
              {debt.dueDateType && (
                <div className="text-xs text-blue-600">
                  Due: {debt.dueDateType === 'First' ? '1st' : debt.dueDateType === 'Last' ? 'Last' : `${debt.dueDateDay}`} of each month
                  {debt.dueDate && ` (Next: ${new Date(debt.dueDate).toLocaleDateString()})`}
                </div>
              )}
            </div>
          )}
        />
        <div className="flex flex-row justify-between items-center w-full mt-12">
          <button className="btn-back" onClick={() => navigate(-1)}>
            Back
          </button>
          <button className="btn-continue" onClick={handleContinue}>
            {isProcessing ? <Spinner text="Processing..." /> : "Save & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebtSetup;
